'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useBarChartStyles } from './useBarCharStyles';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const BarChart = ({ title, data, height = 150, showLegend = true, fillParent, yMin, yMax, yStep }) => {
    const styles = useBarChartStyles();
    const chartRef = useRef(null);
    const chartContainerRef = useRef(null);
    const [hiddenDatasets, setHiddenDatasets] = useState(() => new Set());
    const legendItems = useMemo(() => {
        if (!showLegend)
            return [];
        return data.datasets.map((dataset, index) => ({
            text: dataset.label,
            fillStyle: dataset.backgroundColor,
            strokeStyle: dataset.borderColor || dataset.backgroundColor,
            lineWidth: dataset.borderWidth || 0,
            hidden: hiddenDatasets.has(index),
            index,
            datasetIndex: index
        }));
    }, [showLegend, data, hiddenDatasets]);
    useEffect(() => {
        setHiddenDatasets(new Set());
    }, [data]);
    useLayoutEffect(() => {
        const container = chartContainerRef.current;
        if (!container)
            return;
        const resizeChart = () => {
            chartRef.current?.resize();
        };
        resizeChart();
        const observer = new ResizeObserver(() => {
            resizeChart();
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [data, legendItems.length, height, fillParent, showLegend, yMin, yMax, yStep]);
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false // Disable default legend
            },
            tooltip: {
                enabled: true,
                backgroundColor: '#454F5B',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                cornerRadius: 10,
                padding: 10,
                displayColors: true
            }
        },
        scales: {
            x: {
                barPercentage: 0.5,
                categoryPercentage: 0.8,
                stacked: false,
                grid: {
                    display: false
                },
                ticks: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            y: {
                stacked: false,
                min: yMin,
                max: yMax,
                grid: {
                    display: false
                },
                ticks: {
                    display: false,
                    stepSize: yStep
                },
                border: {
                    display: false
                }
            }
        },
        elements: {
            bar: {
                borderRadius: 50,
                barThickness: 10
            }
        }
    };
    const handleLegendClick = (index) => {
        if (!chartRef.current)
            return;
        const chart = chartRef.current;
        const nextHidden = !chart.isDatasetVisible(index);
        chart.setDatasetVisibility(index, nextHidden);
        chart.update();
        setHiddenDatasets((prev) => {
            const next = new Set(prev);
            if (nextHidden) {
                next.add(index);
            }
            else {
                next.delete(index);
            }
            return next;
        });
    };
    return (_jsxs(Box, { sx: styles.container, children: [title && (_jsx(Box, { mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(Typography, { variant: 'h6', sx: styles.title, children: title }) })), _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: fillParent ? '100%' : (height ?? 200),
                    minHeight: 0
                }, children: [_jsx(Box, { ref: chartContainerRef, sx: { flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }, children: _jsx(Bar, { ref: chartRef, data: data, options: options }) }), showLegend && legendItems.length > 0 && (_jsx(Box, { sx: {
                            flexShrink: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            pb: 1,
                            mt: 1,
                            maxWidth: '100%'
                        }, children: _jsx(Box, { sx: {
                                display: 'flex',
                                gap: 2,
                                minWidth: 'max-content',
                                justifyContent: 'center',
                                px: 1
                            }, children: legendItems.map((item, index) => (_jsxs(Box, { onClick: () => handleLegendClick(index), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    opacity: item.hidden ? 0.4 : 1,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                        opacity: item.hidden ? 0.6 : 0.8
                                    }
                                }, children: [_jsx(Box, { sx: {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: item.fillStyle,
                                            borderRadius: '50%',
                                            flexShrink: 0
                                        } }), _jsx(Typography, { sx: {
                                            whiteSpace: 'nowrap',
                                            fontSize: '10px',
                                            color: '#161C24',
                                            textDecoration: item.hidden ? 'line-through' : 'none',
                                            maxWidth: 160,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }, title: item.text, children: item.text })] }, index))) }) }))] })] }));
};
export default BarChart;
